import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
} from '@mui/material';
import {
  Settings,
  Add,
  Delete,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useSnackbar } from 'notistack';
import { 
  getLevelConfig, 
  saveLevelConfig, 
  resetLevelConfig,
  validateLevelConfig 
} from '../../../../utils/levelConfig';

const LevelConfiguration = ({ levelConfig, onLevelConfigUpdated }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [levelConfigDialog, setLevelConfigDialog] = useState(false);
  const [editingLevelConfig, setEditingLevelConfig] = useState(null);

  const openLevelConfigDialog = () => {
    setEditingLevelConfig(JSON.parse(JSON.stringify(levelConfig))); // Deep copy
    setLevelConfigDialog(true);
  };

  const handleSaveLevelConfig = () => {
    const validation = validateLevelConfig(editingLevelConfig);
    if (!validation.valid) {
      enqueueSnackbar(`Invalid configuration: ${validation.error}`, { variant: 'error' });
      return;
    }

    const saved = saveLevelConfig(editingLevelConfig);
    if (saved) {
      setLevelConfigDialog(false);
      setEditingLevelConfig(null);
      enqueueSnackbar('Level configuration saved!', { variant: 'success' });
      // Refresh user points to update levels
      if (onLevelConfigUpdated) {
        onLevelConfigUpdated();
      }
    } else {
      enqueueSnackbar('Failed to save configuration', { variant: 'error' });
    }
  };

  const handleResetLevelConfig = () => {
    if (!window.confirm('Reset level configuration to defaults? This cannot be undone.')) {
      return;
    }
    
    const reset = resetLevelConfig();
    if (reset) {
      const defaultConfig = getLevelConfig(); // This will now return defaults
      setEditingLevelConfig(defaultConfig);
      enqueueSnackbar('Level configuration reset to defaults', { variant: 'success' });
      // Refresh user points to update levels
      if (onLevelConfigUpdated) {
        onLevelConfigUpdated();
      }
    } else {
      enqueueSnackbar('Failed to reset configuration', { variant: 'error' });
    }
  };

  const updateLevelRequirement = (levelIndex, newPoints) => {
    const updated = { ...editingLevelConfig };
    updated.levels[levelIndex].pointsRequired = parseInt(newPoints) || 0;
    setEditingLevelConfig(updated);
  };

  const addLevel = () => {
    const updated = { ...editingLevelConfig };
    const lastLevel = updated.levels[updated.levels.length - 1];
    const newLevel = {
      level: lastLevel.level + 1,
      pointsRequired: lastLevel.pointsRequired + 100
    };
    updated.levels.push(newLevel);
    setEditingLevelConfig(updated);
  };

  const removeLevel = (levelIndex) => {
    if (editingLevelConfig.levels.length <= 2) {
      enqueueSnackbar('Must have at least 2 levels', { variant: 'warning' });
      return;
    }
    
    const updated = { ...editingLevelConfig };
    updated.levels.splice(levelIndex, 1);
    // Renumber levels
    updated.levels.forEach((level, index) => {
      level.level = index + 1;
    });
    setEditingLevelConfig(updated);
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
      >
        <Box
          sx={{
            bgcolor: '#f8f9fa',
            borderRadius: 4,
            p: 4,
            minHeight: '60vh'
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 4,
              pb: 3,
              borderBottom: '1px solid #e0e0e0'
            }}
          >
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Settings sx={{ color: '#666', fontSize: 28 }} />
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 'bold', 
                    color: '#333',
                    fontFamily: '"Roboto", sans-serif'
                  }}
                >
                  Level Configuration
                </Typography>
              </Box>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: '#666',
                  fontSize: '1rem',
                  opacity: 0.8
                }}
              >
                Customize XP requirements for each level
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={handleResetLevelConfig}
                sx={{
                  color: '#dc3545',
                  borderColor: '#dc3545',
                  fontWeight: 'bold',
                  textTransform: 'none',
                  '&:hover': { 
                    borderColor: '#c82333',
                    bgcolor: '#fff5f5'
                  }
                }}
              >
                Reset to Defaults
              </Button>
              <Button
                variant="contained"
                onClick={openLevelConfigDialog}
                startIcon={<Settings />}
                sx={{
                  bgcolor: '#1976d2',
                  color: 'white',
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 'bold',
                  textTransform: 'none',
                  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                  '&:hover': { 
                    bgcolor: '#1565c0',
                    boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                    transform: 'translateY(-2px)'
                  },
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                Configure Levels
              </Button>
            </Box>
          </Box>

          {/* Level Cards Grid */}
          <Grid container spacing={3}>
            {levelConfig.levels.map((level, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={level.level}>
                <Card
                  sx={{
                    background: 'white',
                    borderRadius: 3,
                    p: 3,
                    height: '160px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    position: 'relative',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                    border: '1px solid rgba(0, 0, 0, 0.05)',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
                    }
                  }}
                >
                  <Box>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 'bold',
                        color: '#333',
                        fontSize: '1.25rem',
                        mb: 1
                      }}
                    >
                      Level {level.level}
                    </Typography>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        color: '#666',
                        fontSize: '1rem',
                        fontWeight: 500
                      }}
                    >
                      {level.pointsRequired} points
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Chip
                      label={`Level ${level.level}`}
                      sx={{
                        bgcolor: '#e3f2fd',
                        color: '#1565c0',
                        fontWeight: 'bold',
                        fontSize: '0.75rem',
                        height: '28px',
                        borderRadius: '14px',
                        border: '1px solid #1565c020'
                      }}
                    />
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </motion.div>

      {/* Level Configuration Dialog */}
      <Dialog
        open={levelConfigDialog}
        onClose={() => {
          setLevelConfigDialog(false);
          setEditingLevelConfig(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Configure Level Requirements
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Set the points required for each level. Levels beyond the configured ones will require{' '}
            {editingLevelConfig?.pointsPerLevelAfter || 500} points each.
          </Typography>
          
          {editingLevelConfig && (
            <TableContainer component={Paper} sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Level</TableCell>
                    <TableCell>Points Required</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {editingLevelConfig.levels.map((level, index) => (
                    <TableRow key={level.level}>
                      <TableCell>{level.level}</TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          value={level.pointsRequired}
                          onChange={(e) => updateLevelRequirement(index, e.target.value)}
                          inputProps={{ min: 0 }}
                          disabled={index === 0} // Can't change level 1
                        />
                      </TableCell>
                      <TableCell>
                        {index > 1 && ( // Can't delete level 1 or 2
                          <IconButton
                            size="small"
                            onClick={() => removeLevel(index)}
                            color="error"
                          >
                            <Delete />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Button
              variant="outlined"
              onClick={addLevel}
              startIcon={<Add />}
            >
              Add Level
            </Button>
            
            <TextField
              size="small"
              label="Points per level after configured"
              type="number"
              value={editingLevelConfig?.pointsPerLevelAfter || 500}
              onChange={(e) => setEditingLevelConfig({
                ...editingLevelConfig,
                pointsPerLevelAfter: parseInt(e.target.value) || 500
              })}
              inputProps={{ min: 1 }}
            />
            
            <TextField
              size="small"
              label="Max Level"
              type="number"
              value={editingLevelConfig?.maxLevel || 50}
              onChange={(e) => setEditingLevelConfig({
                ...editingLevelConfig,
                maxLevel: parseInt(e.target.value) || 50
              })}
              inputProps={{ min: 1, max: 999 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setLevelConfigDialog(false);
              setEditingLevelConfig(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleResetLevelConfig}
            color="warning"
          >
            Reset to Defaults
          </Button>
          <Button
            onClick={handleSaveLevelConfig}
            variant="contained"
            color="primary"
          >
            Save Configuration
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LevelConfiguration;